import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  ISnomedMedication,
  ISnomedMedicationEditionSummary,
  ISnomedMedicationsFilters,
} from "../../../@types/admin/AdminMedication";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { snomedMedicationsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const SNOMED_MEDICATIONS_QUERY_KEY = "admin-snomed-medications";
export const SNOMED_MEDICATIONS_SUMMARY_QUERY_KEY =
  "admin-snomed-medications-summary";

/**
 * A page of SNOMED medication reference rows (EN-5209). Server-paginated and
 * server-sorted — the catalogue is ~30k rows across editions.
 *
 * Runs as soon as the page opens, unlike the Measurements list: the read is
 * indexed and scoped to reference data, so an unfiltered first page is cheap.
 */
export function useSnomedMedications(filters: ISnomedMedicationsFilters) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<ISnomedMedication[]>
  >({
    queryKey: [SNOMED_MEDICATIONS_QUERY_KEY, filters],
    queryFn: async () =>
      ensureOk(await snomedMedicationsService.getMedications(filters)),
    // Keeps the current page on screen while the next one loads, instead of
    // blanking the table on every page step or filter change.
    placeholderData: keepPreviousData,
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list (a new SNOMED
    // release may have been seeded in between).
    refetchOnMount: "always",
  });

  return {
    medications: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/** Per-edition census shown above the list. */
export function useSnomedMedicationSummary() {
  const { data, isLoading, isError } = useQuery<
    ISnomedMedicationEditionSummary[]
  >({
    queryKey: [SNOMED_MEDICATIONS_SUMMARY_QUERY_KEY],
    queryFn: async () => ensureOk(await snomedMedicationsService.getSummary()),
    refetchOnMount: "always",
  });

  return { summary: data ?? [], isLoading, isError };
}
